import React, { useEffect, useState } from 'react'
import {
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useMutation, useQuery } from '@apollo/client'
import { GET_LLM_SETTINGS, UPDATE_LLM_SETTINGS } from '../../../entities/llm-settings/queries'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    maxWidth: 600,
  },
  formControl: {
    minWidth: 200,
  },
  temperatureControl: {
    marginTop: theme.spacing(2),
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(2),
  },
}))

const AVAILABLE_MODELS = [
  { value: 'openai:gpt-4o', label: 'GPT-4o' },
  { value: 'openai:gpt-4o-mini', label: 'GPT-4o Mini' },
  { value: 'openai:gpt-4-turbo', label: 'GPT-4 Turbo' },
  { value: 'openai:gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
]

const DEFAULT_MODEL = 'openai:gpt-4o-mini'
const DEFAULT_TEMPERATURE = 0.7

function LLMSettings() {
  const classes = useStyles()
  const { data, loading: isGettingSettings, refetch } = useQuery(GET_LLM_SETTINGS)
  const [updateLLMSettings, { loading: isUpdatingSettings }] = useMutation(UPDATE_LLM_SETTINGS, {
    onCompleted: refetch,
  })

  const [model, setModel] = useState('')
  const [temperature, setTemperature] = useState(0.7)

  const handleModelChange = event => {
    setModel(event.target.value)
  }

  const handleTemperatureChange = (event, newValue) => {
    setTemperature(newValue)
  }

  const handleSaveSettings = () => {
    try {
      updateLLMSettings({
        variables: {
          data: {
            model,
            temperature,
          },
        },
      })
    } catch (error) {
      console.error('Error updating LLM settings', error)
    }
  }

  const handleResetToDefaults = () => {
    setModel(DEFAULT_MODEL)
    setTemperature(DEFAULT_TEMPERATURE)
  }

  useEffect(() => {
    if (isGettingSettings) return
    setModel(data?.getLLMSettings?.model || DEFAULT_MODEL)
    setTemperature(data?.getLLMSettings?.temperature || DEFAULT_TEMPERATURE)
  }, [isGettingSettings, data])

  return (
    <div className={classes.root}>
      <Typography variant="h2" component="h6">
        LLM Settings
      </Typography>

      <FormControl className={classes.formControl}>
        <InputLabel id="model-select-label">Model</InputLabel>
        <Select
          labelId="model-select-label"
          value={model}
          onChange={handleModelChange}
          disabled={isGettingSettings}
        >
          {AVAILABLE_MODELS.map(modelOption => (
            <MenuItem key={modelOption.value} value={modelOption.value}>
              {modelOption.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className={classes.temperatureControl}>
        <Typography id="temperature-slider" gutterBottom>
          Temperature: {temperature}
        </Typography>
        <Slider
          value={temperature}
          onChange={handleTemperatureChange}
          aria-labelledby="temperature-slider"
          valueLabelDisplay="auto"
          step={0.1}
          marks
          min={0}
          max={2}
          disabled={isGettingSettings}
        />
      </div>

      <div className={classes.buttonGroup}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveSettings}
          disabled={isUpdatingSettings}
        >
          {isUpdatingSettings ? 'Saving...' : 'Save Settings'}
        </Button>

        <Button
          variant="outlined"
          onClick={handleResetToDefaults}
          disabled={isGettingSettings || isUpdatingSettings}
        >
          Reset to Defaults
        </Button>
      </div>
    </div>
  )
}

export default LLMSettings
